import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { InactivityWarningDialog } from '@/components/InactivityWarningDialog';
import { Authentticatevalues, ROUTES, STORAGE_KEYS } from '@/constants';
import { usePreventBackNavigation } from '@/hooks/usePreventBackNavigation';
import { usePreventPullToRefresh } from '@/hooks/usePreventPullToRefresh';
import { useSessionManager } from '@/hooks/useSessionManager';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import { useAppSelector, selectUser, selectPlant, selectSessionId } from '@/store';

// Module-level variable — survives unmount/remount across route navigation.
let pausedElapsed: number | null = null;

function resolveInitialOffset(): number {
  // Priority 1: module-level variable (in-session return from break)
  if (pausedElapsed !== null) return pausedElapsed;

  // Priority 2: persisted paused elapsed (app-killed during break from shuttle)
  const savedPaused = localStorage.getItem(STORAGE_KEYS.shuttleTimerPausedElapsed);
  if (savedPaused) {
    const parsed = parseInt(savedPaused, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }

  // Priority 3: persisted start timestamp (app-killed during active shuttle)
  const savedStart = localStorage.getItem(STORAGE_KEYS.shuttleTimerStart);
  if (savedStart) {
    const parsed = parseInt(savedStart, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return Math.max(0, Math.floor((Date.now() - parsed) / 1000));
    }
  }

  // Priority 4: fresh start
  return 0;
}

export function ShuttleTransferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showWarning, countdown, onContinue } = useSessionManager();

  const initialOffset = resolveInitialOffset();
  const isResuming = initialOffset > 0;

  const [elapsedSeconds, setElapsedSeconds] = useState(initialOffset);
  const startedAtRef = useRef<number>(Date.now() - initialOffset * 1000);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const allowNavigationRef = useRef(false);
  const shuttleLogFiredRef = useRef(false);

  const user = useAppSelector(selectUser);
  const plant = useAppSelector(selectPlant);
  const sessionId = useAppSelector(selectSessionId);

  // Clear module-level and persisted paused elapsed after reading
  useEffect(() => {
    pausedElapsed = null;
    localStorage.removeItem(STORAGE_KEYS.shuttleTimerPausedElapsed);
  }, []);

  // Mark shuttle mode active and persist the effective start timestamp
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.shuttleModeActive, 'true');
    localStorage.setItem(STORAGE_KEYS.shuttleTimerStart, String(startedAtRef.current));
  }, []);

  usePreventPullToRefresh();

  // Log shuttle transfer start only on fresh entry (not when resuming from break)
  useEffect(() => {
    if (isResuming) return;
    if (shuttleLogFiredRef.current) return;
    shuttleLogFiredRef.current = true;

    void UpdateUserLogAsync({
      user: user?._id ?? '',
      status: Authentticatevalues.UserStatusStartShuttleTransfer,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId ?? '',
      plant_id: plant?._id,
    }).catch(() => {
      // Fire-and-forget — don't block the UI if logging fails
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start the interval — startedAtRef and elapsedSeconds are already
  // initialized with the resume offset during component construction.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Re-sync timer when app resumes from background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const formatTime = useCallback((totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return {
      hours,
      hoursStr: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    };
  }, []);

  const handleTakeBreak = useCallback(async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    const currentElapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
    pausedElapsed = currentElapsed;
    localStorage.setItem(STORAGE_KEYS.shuttleTimerPausedElapsed, String(currentElapsed));

    try {
      await UpdateUserLogAsync({
        user: user?._id ?? '',
        status: Authentticatevalues.UserStatusStopShuttleTransfer,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant?._id,
      });
    } catch {
      // Fire-and-forget
    }

    navigate(`${ROUTES.breakMode}?from=shuttle`);
  }, [navigate, user?._id, sessionId, plant?._id]);

  const handleSwitchToYardOperations = useCallback(async () => {
    allowNavigationRef.current = true;
    pausedElapsed = null;

    localStorage.removeItem(STORAGE_KEYS.shuttleModeActive);
    localStorage.removeItem(STORAGE_KEYS.shuttleTimerStart);
    localStorage.removeItem(STORAGE_KEYS.shuttleTimerPausedElapsed);

    try {
      await UpdateUserLogAsync({
        user: user?._id ?? '',
        status: Authentticatevalues.UserStatusStopShuttleTransfer,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant?._id,
      });
      await UpdateUserLogAsync({
        user: user?._id ?? '',
        status: Authentticatevalues.UserStatusStartYardWork,
        activity_date_time: new Date().toISOString(),
        session_id: sessionId ?? '',
        plant_id: plant?._id,
      });
    } catch {
      // Fire-and-forget — navigate regardless
    }

    navigate(ROUTES.dashboard);
  }, [navigate, user?._id, sessionId, plant?._id]);

  // Silently absorb back button — PWA cannot minimize, so back does nothing.
  // User must use on-screen buttons to leave shuttle transfer.
  usePreventBackNavigation(true, allowNavigationRef);

  const { hours, hoursStr, minutes, seconds } = formatTime(elapsedSeconds);

  return (
    <div className="flex flex-col min-h-screen bg-white px-4 sm:px-16 py-8">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center justify-center bg-white">
        <h1 className="font-poppins font-normal text-black text-2xl sm:text-4xl text-center">
          {t('shuttleTransfer.title', 'Shuttle/Transfer')}
        </h1>
      </header>

      {/* Content */}
      <div className="flex flex-col items-center justify-center">
        {/* Timer */}
        <div className="mt-16 flex items-baseline gap-1">
          {hours > 0 && (
            <>
              <span className="font-poppins text-4xl font-normal text-[#003366]">{hoursStr}</span>
              <span className="font-poppins text-4xl font-normal text-[#003366]">
                {t('timer.h')}
              </span>
              <span className="font-poppins text-4xl font-normal text-[#003366] mx-1">:</span>
            </>
          )}
          <span className="font-poppins text-4xl font-normal text-[#003366]">{minutes}</span>
          <span className="font-poppins text-4xl font-normal text-[#003366]">{t('timer.m')}</span>
          <span className="font-poppins text-4xl font-normal text-[#003366] mx-1">:</span>
          <span className="font-poppins text-4xl font-normal text-[#003366]">{seconds}</span>
          <span className="font-poppins text-4xl font-normal text-[#003366]">{t('timer.s')}</span>
        </div>

        {/* Take a Break Button */}
        <button
          onClick={() => void handleTakeBreak()}
          className="mt-8 w-auto flex items-center gap-1.5 px-8 py-2.5 rounded-xl border-[#e38d24] border-[0.8px] bg-[#faedde] text-[#e3912d] font-poppins text-[13px] font-medium"
        >
          <img src="/images/take_break_icon.svg" alt="" className="w-6 h-6  object-contain" />
          {t('shuttleTransfer.takeBreak', 'Take a Break')}
        </button>

        {/* Truck Illustration */}
        <div className="flex items-center justify-center">
          <img
            src="/images/shuttle_transfer_illustration.svg"
            alt=""
            className="h-[450px] w-[450px]"
          />
        </div>
      </div>

      {/* Switch to Yard Operations Button - Fixed at bottom */}
      <div className="flex items-center justify-center mt-auto">
        <button
          onClick={() => void handleSwitchToYardOperations()}
          className="w-full flex gap-1 items-center justify-center py-3 px-16 rounded-xl border-[0.9px] bg-white text-[#2c6aab] border-[#1358a1] font-poppins text-[13px] font-medium"
        >
          <img src="/images/switch_to_yard_icon.svg" alt="Shuttle truck" className="h-5 w-5" />
          {t('shuttleTransfer.switchToYard', 'Switch to Yard Operations')}
        </button>
      </div>

      <InactivityWarningDialog open={showWarning} countdown={countdown} onContinue={onContinue} />
    </div>
  );
}
